import { EditableText } from '@/components/editor/EditableText';
import { FICHE_COLOR_HEX } from '@/lib/colors';
import type { FicheColor } from '@pharmafiche/schema';

interface CardHeadProps {
  num: number;
  icon?: string;
  label: string;
  color: FicheColor;
  /** Tighter head (smaller font, circle, padding). Auto for drug cards. */
  dense?: boolean;
  /** When true, the label becomes contenteditable in edit mode. */
  editableTitle?: boolean;
  /** Commit handler when editableTitle is set. */
  onTitleCommit?: (next: string) => void;
}

/**
 * The pill-shaped card head that overlaps the top edge of a Dessinée card.
 * Sizes to content but never exceeds the card width.
 */
export function CardHead({
  num,
  icon,
  label,
  color,
  dense = false,
  editableTitle = false,
  onTitleCommit,
}: CardHeadProps) {
  const accent = FICHE_COLOR_HEX[color];
  const fontPx = dense ? 14 : 17;
  const circleSize = dense ? 20 : 24;
  const paddingX = dense ? 10 : 14;
  const marginTop = dense ? -22 : -28;

  return (
    <div
      className="flex items-center gap-2 bg-white border-[2.5px] border-ink rounded-full font-gloock shadow-ink-sm"
      style={{
        width: 'fit-content',
        maxWidth: '100%',
        paddingLeft: paddingX,
        paddingRight: paddingX,
        paddingTop: 4,
        paddingBottom: 2,
        fontSize: fontPx,
        margin: `${marginTop}px 0 10px`,
      }}
    >
      <span
        className="font-gloock inline-grid place-items-center text-white shrink-0"
        style={{
          background: accent,
          width: circleSize,
          height: circleSize,
          fontSize: dense ? 13 : 16,
          borderRadius: '50%',
          border: '2px solid #1f2330',
          lineHeight: 1,
        }}
      >
        {num}
      </span>
      {icon ? (
        <span className="leading-none shrink-0" style={{ fontSize: dense ? 15 : 18 }}>
          {icon}
        </span>
      ) : null}
      {editableTitle ? (
        <EditableText
          value={label}
          plain
          ariaLabel="Titre de la carte"
          style={{
            color: accent,
            minWidth: 0,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            hyphens: 'auto',
            lineHeight: 1.1,
          }}
          onCommit={(next) => onTitleCommit?.(next)}
        />
      ) : (
        <span
          lang="fr"
          style={{
            color: accent,
            minWidth: 0,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            hyphens: 'auto',
            lineHeight: 1.1,
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
}
